import { useEffect, useState } from 'react'
import './CopraManagementTool.css'
import {
  authedDelete,
  authedGet,
  authedPost,
  authedPut,
} from '../lib/intranet_tools_service'
import { EditModal } from './EditModal'
import { NewModal } from './NewModal'
import { ProjectModal } from './ProjectModal'
import {
  AVProject,
  NewProfessional,
  ProfessionalDetail,
  ProfessionalProject,
} from './types'
import {
  DEFAULT_NEW_PROFESSIONAL,
  createDefaultProject,
  mapProfessionalDetail,
  mapProfessionalProjectToDetail,
  normalizeProjectDates,
  reverseDate,
  validateProjectInputs,
} from './util'

const parseDateOrNull = (sdate: string | null) => {
  if (sdate === null) return 0
  return new Date(sdate).getTime()
}

const flip = (order: string) => {
  if (order === 'DESC') {
    return 'ASC'
  }
  return 'DESC'
}

const STORAGE_PROFESSIONAL_PROJECTS_SORTING = 'professional_projects_sorting'
const STORAGE_PROFESSIONAL_PROJECTS_FILTER = 'professional_projects_filter'

const getDefaultSortBy = () => {
  const professionalprojectsSortBy = localStorage.getItem(
    STORAGE_PROFESSIONAL_PROJECTS_SORTING
  )
  if (professionalprojectsSortBy) {
    try {
      return JSON.parse(professionalprojectsSortBy)
    } catch {
      localStorage.removeItem(STORAGE_PROFESSIONAL_PROJECTS_SORTING)
    }
  }
  return ['name', 'ASC']
}

const getFilter = () => {
  const professionalprojectsFilter = localStorage.getItem(
    STORAGE_PROFESSIONAL_PROJECTS_FILTER
  )
  return professionalprojectsFilter || ''
}

const containsInsensitive = (string1: string, string2: string): boolean => {
  return string1.toLowerCase().indexOf(string2.toLowerCase()) !== -1
}

const getErrorMessage = async (result: Response, fallback: string) => {
  try {
    const json = await result.json()
    return json.error ? `${fallback}: ${json.error}` : fallback
  } catch {
    return fallback
  }
}

export const CopraManagementTool = () => {
  const [professionalProjects, setProfessionalProjects] =
    useState<ProfessionalProject[]>()
  const [selectedProfessional, setSelectedProfessional] =
    useState<ProfessionalDetail>()
  const [newProfessional, setNewProfessional] = useState<NewProfessional>()
  const [projectDraft, setProjectDraft] = useState<AVProject>()
  const [projectApprovers, setProjectApprovers] = useState<string[]>([])
  const [error, setError] = useState('')
  const [projectError, setProjectError] = useState('')
  const [sortBy, setSortBy] = useState(getDefaultSortBy())
  const [filterBy, setFilter] = useState(getFilter())

  const loadProfessionalProjects = async () => {
    const result = await authedGet('av/professionals')
    if (!result.ok) {
      setError(await getErrorMessage(result, 'Error loading professionals'))
      return
    }

    const json = await result.json()
    setError('')
    setProfessionalProjects(json.data)
  }

  const loadProfessionalDetail = async (
    id: number,
    fallbackProfessional?: ProfessionalProject
  ) => {
    const result = await authedGet(`av/professionals/${id}`)
    if (!result.ok) {
      setError(await getErrorMessage(result, 'Error loading professional'))
      return
    }

    const json = await result.json()
    const detail = mapProfessionalDetail(json.data)
    if (!detail) {
      if (fallbackProfessional) {
        setSelectedProfessional(mapProfessionalProjectToDetail(fallbackProfessional))
        setError('')
        return
      }
      setError('Professional details could not be loaded')
      return
    }

    setError('')
    setSelectedProfessional(detail)
  }

  const reloadAfterSave = async (professionalId?: number) => {
    await loadProfessionalProjects()
    if (professionalId) {
      await loadProfessionalDetail(professionalId)
    }
  }

  const setSorting = (column: string) => {
    let newSortBy
    if (column === sortBy[0]) {
      newSortBy = [column, flip(sortBy[1])]
    } else {
      newSortBy = [column, 'ASC']
    }
    setSortBy(newSortBy)
    localStorage.setItem(
      STORAGE_PROFESSIONAL_PROJECTS_SORTING,
      JSON.stringify(newSortBy)
    )
  }

  const applyFilter = (sfilter: string) => {
    setFilter(sfilter)
    localStorage.setItem(STORAGE_PROFESSIONAL_PROJECTS_FILTER, sfilter)
  }

  useEffect(() => {
    loadProfessionalProjects()
  }, [])

  const onClickRow = async (professionalProject: ProfessionalProject) => {
    setSelectedProfessional(mapProfessionalProjectToDetail(professionalProject))
    await loadProfessionalDetail(professionalProject.ID, professionalProject)
  }

  const onCreateProfessional = async (professional: NewProfessional) => {
    const result = await authedPost(
      'av/professionals',
      JSON.stringify(professional)
    )
    if (!result.ok) {
      setError(await getErrorMessage(result, 'Error creating professional'))
      return
    }

    const json = await result.json()
    setError('')
    setNewProfessional(undefined)
    await reloadAfterSave(json.data?.ID)
  }

  const onSaveProfessional = async (professional: ProfessionalDetail) => {
    if (!professional.UserID && professional.Email) {
      if (
        !window.confirm(
          'Setting an email for the professional will create an account in COPRA and email them a link to activate it. Please confirm?'
        )
      ) {
        return
      }
    }

    const currentRow = professionalProjects?.find(
      (item) => item.ID === professional.ID
    )
    const oldEmail = currentRow?.Email
    const isEmailChange = !!oldEmail && professional.Email !== oldEmail

    if (isEmailChange) {
      if (
        !window.confirm(
          'WARNING! This update has a security risk. Verify the new email belongs to the professional before changing it.\nChanging an email will result in a new activation mail being sent to the new email address.'
        )
      ) {
        return
      }
    }

    const result = await authedPut(
      `av/professionals/${professional.ID}`,
      JSON.stringify({
        ...professional,
        IsEmailChange: isEmailChange,
      })
    )

    if (!result.ok) {
      setError(await getErrorMessage(result, 'Error updating professional'))
      return
    }

    setError('')
    await reloadAfterSave(professional.ID)
  }

  const onDeleteProfessional = async () => {
    if (!selectedProfessional) return

    if (!window.confirm('Are you sure you want to remove this professional?')) {
      return
    }

    const result = await authedDelete(`av/professionals/${selectedProfessional.ID}`)
    if (!result.ok) {
      setError(await getErrorMessage(result, 'Error deleting professional'))
      return
    }

    setSelectedProfessional(undefined)
    setError('')
    await loadProfessionalProjects()
  }

  const onResetPassword = async () => {
    if (!selectedProfessional) return

    if (
      !window.confirm(
        "This will send a password reset link to the professional's email address. Are you sure?"
      )
    ) {
      return
    }

    const result = await authedPut(
      `av/professionals/${selectedProfessional.ID}/reset-password`,
      JSON.stringify({})
    )

    if (!result.ok) {
      setError(await getErrorMessage(result, 'Error resetting password'))
      return
    }

    setError('Password reset mail sent successfully.')
  }

  const onNewProject = () => {
    if (!selectedProfessional) return

    setProjectError('')
    setProjectDraft(createDefaultProject(selectedProfessional.ID))
    setProjectApprovers([])
  }

  const onEditProject = async (project: AVProject) => {
    setProjectError('')
    const result = await authedGet(`av/projects/${project.ID}`)

    if (!result.ok) {
      setProjectError(await getErrorMessage(result, 'Error loading project'))
      setProjectDraft(normalizeProjectDates(project))
      setProjectApprovers(
        (project.Approvers || []).map((approver) => approver.ID || '')
      )
      return
    }

    const json = await result.json()
    const loadedProject = normalizeProjectDates({
      ...json.data,
      Approvers: json.data?.Approvers || [],
    } as AVProject)

    setProjectDraft(loadedProject)
    setProjectApprovers(
      (loadedProject.Approvers || []).map((approver) => approver.ID || '')
    )
  }

  const onSaveProject = async (project: AVProject) => {
    const validationError = validateProjectInputs(project)
    if (validationError) {
      setProjectError(validationError)
      return
    }

    const payload = {
      ...project,
      NewApproversList: projectApprovers.filter(Boolean),
    }

    const result = project.ID
      ? await authedPut(`av/projects/${project.ID}`, JSON.stringify(payload))
      : await authedPost('av/projects', JSON.stringify(payload))

    if (!result.ok) {
      setProjectError(await getErrorMessage(result, 'Error saving project'))
      return
    }

    setProjectError('')
    setProjectDraft(undefined)
    await reloadAfterSave(project.AVProfessionalID || selectedProfessional?.ID)
  }

  const sorted = professionalProjects
    ? [...professionalProjects]
        .sort((a, b) => {
          switch (sortBy[0]) {
            case 'dateStart':
              return sortBy[1] === 'ASC'
                ? parseDateOrNull(a.StartDate) - parseDateOrNull(b.StartDate)
                : parseDateOrNull(b.StartDate) - parseDateOrNull(a.StartDate)
            case 'dateEnd':
              return sortBy[1] === 'ASC'
                ? parseDateOrNull(a.EndDate) - parseDateOrNull(b.EndDate)
                : parseDateOrNull(b.EndDate) - parseDateOrNull(a.EndDate)
            case 'name':
            default:
              const nameDiff = a.LastName.localeCompare(b.LastName)
              return sortBy[1] === 'ASC' ? nameDiff : -nameDiff
          }
        })
        .filter((value) => {
          return (
            !filterBy ||
            containsInsensitive(value.LastName, filterBy) ||
            containsInsensitive(value.FirstName, filterBy)
          )
        })
    : undefined

  const widths = [150, 400, 150, 150, 150, 120, 170]

  return (
    <>
      {newProfessional && (
        <NewModal
          onClose={() => setNewProfessional(undefined)}
          onSave={onCreateProfessional}
          professional={newProfessional}
          setProfessional={setNewProfessional}
        />
      )}
      {selectedProfessional && (
        <EditModal
          onClose={() => setSelectedProfessional(undefined)}
          onDelete={onDeleteProfessional}
          onNewProject={onNewProject}
          onResetPassword={onResetPassword}
          onSave={onSaveProfessional}
          onSelectProject={onEditProject}
          professional={selectedProfessional}
          setProfessional={setSelectedProfessional}
        />
      )}
      {projectDraft && (
        <ProjectModal
          approvers={projectApprovers}
          error={projectError}
          onClose={() => {
            setProjectDraft(undefined)
            setProjectError('')
          }}
          onSave={onSaveProject}
          project={projectDraft}
          setApprovers={setProjectApprovers}
          setProject={setProjectDraft}
        />
      )}
      <div className="christmas-gifts-container">
        <div className="christmas-gifts-title">
          <div className="flex flex-justify-between">
            <div className="flex flex-container">
              <div>Professionals overview</div>
              <div className="filter">
                Filter
                <input
                  className="input-filter"
                  id="filter"
                  value={filterBy}
                  onChange={(e) => applyFilter(e.target.value)}
                />
              </div>
            </div>
            <button
              className="cg-button"
              onClick={() => setNewProfessional(DEFAULT_NEW_PROFESSIONAL)}
            >
              New
            </button>
          </div>
        </div>
        {error && <div className="copra-feedback">{error}</div>}
        <div className="table">
          <div className="flex table-row pm-table-header">
            <div style={{ width: widths[0] }}>ID</div>
            <div
              className="cursor-pointer"
              style={{ width: widths[1] }}
              onClick={() => setSorting('name')}
            >
              Professional
            </div>
            <div style={{ width: widths[2] }}>Project Code</div>
            <div
              className="cursor-pointer"
              style={{ width: widths[3] }}
              onClick={() => setSorting('dateStart')}
            >
              Start date
            </div>
            <div
              className="cursor-pointer"
              style={{ width: widths[4] }}
              onClick={() => setSorting('dateEnd')}
            >
              End date
            </div>
            <div style={{ width: widths[5] }}>Reimbursed</div>
            <div style={{ width: widths[6] }}>Deductible</div>
          </div>
          {sorted &&
            sorted.map((professionalProject) => (
              <div
                key={`${professionalProject.ID}_${professionalProject.ProjectCode}_${professionalProject.StartDate}`}
                className="flex pm-table-row"
                onClick={() => onClickRow(professionalProject)}
              >
                <div style={{ width: widths[0] }}>{professionalProject.ID}</div>
                <div style={{ width: widths[1] }}>
                  {professionalProject.LastName}, {professionalProject.FirstName}
                </div>
                <div style={{ width: widths[2] }}>
                  {professionalProject.ProjectCode}
                </div>
                <div style={{ width: widths[3] }}>
                  {reverseDate(professionalProject.StartDate)}
                </div>
                <div style={{ width: widths[4] }}>
                  {reverseDate(professionalProject.EndDate)}
                </div>
                <div style={{ width: widths[5] }}>
                  {professionalProject.PRJID &&
                  professionalProject.ExpensesReimbursed
                    ? 'Exp '
                    : ''}
                  {professionalProject.PRJID && professionalProject.TravelReimbursed
                    ? 'KM'
                    : ''}
                </div>
                <div style={{ width: widths[6] }}>
                  {professionalProject.PRJID && professionalProject.TravelDeductible
                    ? 'KM '
                    : ''}
                  {professionalProject.PRJID &&
                  professionalProject.TravelExpensesDeductible
                    ? 'TrvExp '
                    : ''}
                  {professionalProject.PRJID &&
                  professionalProject.ExpensesDeductible
                    ? 'OthExp '
                    : ''}
                  {professionalProject.PRJID && professionalProject.WorkedFromHome
                    ? 'WFH'
                    : ''}
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  )
}
